import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StudyContentViewer from '../components/study/StudyContentViewer';
import CommentSection from '../components/study/CommentSection';
import { studyApi, entryApi } from '../utils/studyApiService';
import './StudyDetailPage.css';

const StudyDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [study, setStudy] = useState(null);
  const [entries, setEntries] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loadingStudy, setLoadingStudy] = useState(true);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [error, setError] = useState('');

  const fetchStudy = useCallback(async () => {
    setLoadingStudy(true);
    try {
      const res = await studyApi.get(id);
      if (res.success) setStudy(res.data);
    } catch (e) {
      setError(e.message || '스터디를 불러오지 못했습니다.');
    } finally {
      setLoadingStudy(false);
    }
  }, [id]);

  const fetchEntries = useCallback(async () => {
    setLoadingEntries(true);
    try {
      const res = await entryApi.list(id);
      if (res.success) setEntries(res.data.items || []);
    } catch (_) {}
    finally {
      setLoadingEntries(false);
    }
  }, [id]);

  useEffect(() => { fetchStudy(); }, [fetchStudy]);
  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleDeleteStudy = async () => {
    if (!window.confirm('스터디를 삭제하시겠습니까? 모든 엔트리도 함께 삭제됩니다.')) return;
    try {
      await studyApi.delete(id);
      navigate('/studies');
    } catch (e) {
      alert(e.message || '삭제에 실패했습니다.');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('엔트리를 삭제하시겠습니까?')) return;
    try {
      await entryApi.delete(id, entryId);
      if (selectedEntry?.id === entryId) setSelectedEntry(null);
      fetchEntries();
      fetchStudy();
    } catch (e) {
      alert(e.message || '엔트리 삭제에 실패했습니다.');
    }
  };

  const handleSelectEntry = async (entry) => {
    if (selectedEntry?.id === entry.id) {
      setSelectedEntry(null);
      return;
    }
    try {
      const res = await entryApi.get(id, entry.id);
      if (res.success) setSelectedEntry(res.data);
    } catch (_) {
      setSelectedEntry(entry);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return String(iso).slice(0, 10);
  };

  const formatDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  if (loadingStudy) return <div className="sdp-loading">불러오는 중...</div>;
  if (error) return <div className="sdp-error">{error}</div>;
  if (!study) return null;

  const templateFields = study.template?.schema?.fields || [];

  return (
    <div className="sdp-page">
      {/* 네비게이션 */}
      <div className="sdp-nav">
        <button className="sdp-back-btn" onClick={() => navigate('/studies')}>← 목록으로</button>
        <div className="sdp-actions">
          <button className="sdp-btn sdp-btn-edit" onClick={() => navigate(`/studies/${id}/edit`)}>수정</button>
          <button className="sdp-btn sdp-btn-delete" onClick={handleDeleteStudy}>삭제</button>
        </div>
      </div>

      {/* 스터디 메타 */}
      <div className="sdp-meta-section">
        <h1 className="sdp-title">{study.title}</h1>
        {study.description && (
          <p className="sdp-description">{study.description}</p>
        )}
        <div className="sdp-meta">
          {study.template && (
            <span className="sdp-template">
              템플릿: {study.template.name} v{study.template.version}
            </span>
          )}
          <span className="sdp-meta-item">엔트리 {study.entry_count || entries.length}개</span>
          {study.latest_entry_date && (
            <span className="sdp-meta-item">최근 기록: {formatDate(study.latest_entry_date)}</span>
          )}
          <span className="sdp-meta-item">생성: {formatDateTime(study.created_at)}</span>
        </div>
        {study.tags?.length > 0 && (
          <div className="sdp-tags">
            {study.tags.map((tag, i) => (
              <span key={i} className="sdp-tag">#{tag}</span>
            ))}
          </div>
        )}
      </div>

      {/* 엔트리 섹션 */}
      <div className="sdp-entries-section">
        <div className="sdp-entries-header">
          <h2 className="sdp-entries-title">분석 기록</h2>
          <button
            className="sdp-btn sdp-btn-add"
            onClick={() => navigate(`/studies/${id}/entries/new`)}
          >
            + 새 엔트리
          </button>
        </div>

        {loadingEntries ? (
          <div className="sdp-entries-loading">불러오는 중...</div>
        ) : entries.length === 0 ? (
          <div className="sdp-entries-empty">
            <p>아직 분석 기록이 없습니다.</p>
            <button
              className="sdp-btn sdp-btn-add"
              onClick={() => navigate(`/studies/${id}/entries/new`)}
            >
              첫 엔트리 작성하기
            </button>
          </div>
        ) : (
          <div className="sdp-entry-list">
            {entries.map((entry) => (
              <div key={entry.id} className="sdp-entry-item">
                {/* 엔트리 행 */}
                <div
                  className={`sdp-entry-row ${selectedEntry?.id === entry.id ? 'sdp-entry-row--active' : ''}`}
                  onClick={() => handleSelectEntry(entry)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleSelectEntry(entry)}
                >
                  <div className="sdp-entry-date">{formatDate(entry.entry_date)}</div>
                  <div className="sdp-entry-summary">
                    {entry.note && <span className="sdp-entry-note">{entry.note}</span>}
                    {entry.content_summary && Object.entries(entry.content_summary).map(([k, v]) => (
                      <span key={k} className="sdp-entry-summary-item">
                        <span className="sdp-entry-summary-key">{k}</span>
                        <span className="sdp-entry-summary-val">
                          {typeof v === 'boolean' ? (v ? '✓' : '✗') :
                           typeof v === 'number' ? v.toLocaleString() :
                           Array.isArray(v) ? v.join(', ') :
                           String(v ?? '')}
                        </span>
                      </span>
                    ))}
                  </div>
                  <div className="sdp-entry-meta">
                    <span className="sdp-entry-comment-count">💬 {entry.comment_count || 0}</span>
                    <div className="sdp-entry-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="sdp-entry-btn"
                        onClick={() => navigate(`/studies/${id}/entries/${entry.id}/edit`)}
                      >
                        수정
                      </button>
                      <button
                        className="sdp-entry-btn sdp-entry-btn-delete"
                        onClick={() => handleDeleteEntry(entry.id)}
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                </div>

                {/* 엔트리 상세 (선택 시 인라인 표시) */}
                {selectedEntry?.id === entry.id && (
                  <div className="sdp-entry-detail">
                    {templateFields.length > 0 && selectedEntry.content && (
                      <div className="sdp-entry-content">
                        <StudyContentViewer
                          fields={templateFields}
                          content={selectedEntry.content || {}}
                        />
                      </div>
                    )}
                    {selectedEntry.note && (
                      <div className="sdp-entry-note-full">
                        <div className="sdp-entry-note-label">메모</div>
                        <p className="sdp-entry-note-text">{selectedEntry.note}</p>
                      </div>
                    )}
                    <div className="sdp-entry-comments">
                      <CommentSection studyId={id} entryId={entry.id} />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudyDetailPage;
